/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import SqlImportRowModel from 'cio/models/sql-import-row';
import MomentService from 'cio/services/moment';
import { inject as service } from '@ember/service';
import Ember from 'ember';
import Sprite from 'ember-animated/-private/sprite';
import { wait } from 'ember-animated';
import { action, computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import AnalyticsService from 'cio/services/analytics';
import { dropTask, Task } from 'ember-concurrency';
import ApiErrorsService from 'cio/services/api-errors';
import ApiService from 'cio/services/api';
import { formatNumber } from 'cio/helpers/format-number';
import pluralize from 'cio/utils/pluralize';
import joinWords from 'cio/utils/join-words';

interface SqlImportsImportRowsRowsArgs {
  cancelImportTask: Task<unknown, []>;
  row: SqlImportRowModel;
  isExpanded: boolean;
  expand: () => unknown;
  readOnly: boolean;
}

const COLLAPSE_DURATION = 150;

export default class SqlImportsImportRowsRows extends Component<SqlImportsImportRowsRowsArgs> {
  @service moment!: MomentService;
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;

  @tracked showCancelImportModal = false;

  get combinedTotalsForHeader() {
    let { addedRows, failedRows, updatedRows, syncTarget, importType } = this.args.row;
    let total: string[] = [];
    let amountOfRows = addedRows + updatedRows + failedRows;
    let getLabel = (label: string) =>
      importType === 'relationship' ? `${pluralize(amountOfRows, 'relationship')} synced` : label;
    if (addedRows) {
      total = [`<strong>${formatNumber([addedRows])}</strong> ${getLabel('new')}`];
    }
    if (updatedRows) {
      total = [...total, `<strong>${formatNumber([updatedRows])}</strong> ${getLabel('existing')}`];
    }
    if (failedRows) {
      total = [...total, `<strong>${formatNumber([failedRows])}<strong> ${getLabel('failed')}`];
    }

    let syncTargetLabel = importType === 'relationship' ? '' : syncTarget;

    return `${joinWords(total)} ${pluralize(amountOfRows, syncTargetLabel)}`;
  }

  @computed('moment.currentTime', 'args.row.createdAt')
  get duration() {
    return this.moment.formatTimestamp([this.args.row.createdAt], {
      capitalize: false,
      finalFormatter: (timestamp: any) => timestamp.fromNow().replace('ago', '').replace('a ', '').trim(),
    });
  }

  *collapseTransition({ removedSprites }: { removedSprites: Sprite[] }) {
    // eslint-disable-next-line
    for (let _ of removedSprites) {
      if (!Ember.testing) {
        yield wait(COLLAPSE_DURATION);
      }
    }
  }

  @computed('args.row.segment.{id,name}')
  get belongsToSegment() {
    return this.args.row.belongsTo('segment').value();
  }

  downloadFileTask = dropTask(async () => {
    try {
      this.analytics.track('sql_imports', 'download_error_file', { model: this.args.row.import });

      let downloadPath = `exports/${this.args.row.errorExportId}/download`;
      let { url } = await this.api.post(downloadPath);
      window.location = url;
    } catch (error) {
      this.apiErrors.report(error);
    }
  });

  @action
  toggleCancelImportModal() {
    if (!this.showCancelImportModal) {
      this.analytics.track('sql_imports', 'cancel_import', { model: this.args.row.import });
    }
    this.showCancelImportModal = !this.showCancelImportModal;
  }
}
